// src/pages/PricingPage.jsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import PricingCard from '../components/ui/PricingCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import Confetti from 'react-confetti';

// ------------- CONFIGURACIÓN DE LANZAMIENTO -------------
const IS_LAUNCH_PROMO_ACTIVE = true; // CAMBIA A false CUANDO TERMINE LA PROMO
const LAUNCH_PROMO_END_DATE = "July 31, 2025"; // Actualiza esta fecha
const CONFETTI_ACTIVE_DURATION = 7000; // Duración durante la cual se generan nuevas piezas (7 segundos)
// ----------------------------------------------------

const PricingPage = () => {
    // Hook para el tamaño de la ventana (sin 'react-use')
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Establecer tamaño inicial
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Estados para controlar el confeti
    const [renderConfettiCanvas, setRenderConfettiCanvas] = useState(false); // Controla si el componente <Confetti/> se renderiza en el DOM
    const [recycleConfettiParticles, setRecycleConfettiParticles] = useState(false); // Controla la prop 'recycle' de <Confetti/>

    useEffect(() => {
        let recycleTimer = null;

        if (IS_LAUNCH_PROMO_ACTIVE) {
            setRenderConfettiCanvas(true); // Mostrar el canvas de confeti
            setRecycleConfettiParticles(true); // Empezar a generar y reciclar partículas

            // Después de CONFETTI_ACTIVE_DURATION, detener la generación de nuevas partículas
            recycleTimer = setTimeout(() => {
                setRecycleConfettiParticles(false);
            }, CONFETTI_ACTIVE_DURATION);
        } else {
            // Si la promo no está activa, asegurarse de que no se renderice nada de confeti
            setRenderConfettiCanvas(false);
            setRecycleConfettiParticles(false);
        }

        return () => {
            clearTimeout(recycleTimer); // Limpiar el timer si el componente se desmonta
        };
    }, []); // Se ejecuta solo una vez al montar el componente

    // (El resto de tu lógica de `basePricingPlans`, `effectivePricingPlans`, etc., permanece igual)
    const basePricingPlans = [
        {
            id: "free",
            planName: "Free",
            price: "0",
            priceFrequency: "/ month",
            description: "Essential features to kickstart your AI-powered health journey.",
            features: [
                { text: "5 AI food analyses per month", included: true },
                { text: "Limited recipe generation (3 per month)", included: true },
                { text: "Basic meal suggestions", included: true },
                { text: "Access to standard workout routines", included: true },
                { text: "Community support", included: true },
                { text: "Unlimited barcode scans", included: false },
                { text: "Personalized meal plans & workout routines", included: false },
                { text: "Advanced nutritional insights", included: false },
                { text: "Priority support", included: false },
            ],
            buttonText: "Get Started Free",
            buttonVariant: "outline-primary",
            buttonLink: "/register"
        },
        {
            id: "pro",
            planName: "Pro",
            price: "19.99",
            priceFrequency: "/ month",
            description: "Unlock the full power of MyFit Menu AI with unlimited access and premium features.",
            features: [
                { text: "Unlimited AI food analyses", included: true },
                { text: "Unlimited recipe generation", included: true },
                { text: "Fully personalized meal plans", included: true },
                { text: "Customizable workout routines", included: true },
                { text: "Unlimited barcode scans", included: true },
                { text: "Advanced nutritional insights & tracking", included: true },
                { text: "Save & organize favorite meals & recipes", included: true },
                { text: "Priority email support", included: true },
            ],
            buttonText: "Go Pro",
            buttonVariant: "primary",
            buttonLink: "/signup",
            isPopular: true,
        },
        {
            id: "business",
            planName: "Business",
            price: "49.99",
            priceFrequency: "/ month",
            description: "Advanced tools and support for professionals and teams dedicated to client success.",
            features: [
                { text: "All Pro features, plus:", included: true, isTitle: true },
                { text: "Manage multiple client profiles (up to 5)", included: true },
                { text: "Team collaboration tools", included: true },
                { text: "Advanced client progress analytics & reporting", included: true },
                { text: "API access (coming soon)", included: true },
                { text: "Dedicated account manager", included: true },
                { text: "Premium 24/7 support", included: true },
            ],
            buttonText: "Contact Sales",
            buttonVariant: "success",
            buttonLink: "/contact-sales"
        }
    ];

    let effectivePricingPlans = basePricingPlans;
    let pageTitle = "Choose Your Perfect Plan";
    let pageSubtitle = "Find the ideal plan for your health journey or business needs and start transforming today!";
    let promoAlertMessage = null;

    if (IS_LAUNCH_PROMO_ACTIVE) {
        pageTitle = "🎉 Special Launch Offer! 🎉";
        pageSubtitle = `Get FULL access to all our Pro features ABSOLUTELY FREE for a limited time! No credit card required. Offer valid for new sign-ups until ${LAUNCH_PROMO_END_DATE}.`;
        promoAlertMessage = (
            <Alert variant="info" className="text-center fs-5 shadow-sm">
                <FontAwesomeIcon icon={faGift} className="me-2" />
                <strong>Launch Special:</strong> All Pro features are currently FREE for everyone!
                Sign up now and enjoy the complete MyFit Menu AI experience.
            </Alert>
        );

        effectivePricingPlans = basePricingPlans.map(plan => {
            if (plan.id === "pro") {
                return {
                    ...plan,
                    price: "0",
                    originalPrice: plan.price,
                    description: "Experience all Pro benefits for FREE during our launch promotion! Everything you need for a personalized fitness and nutrition journey.",
                    buttonText: "Get Full Access FREE",
                    buttonLink: "/register",
                    isPopular: true,
                    promoNotice: "LAUNCH OFFER: 100% FREE"
                };
            }
            if (plan.id === "free" && IS_LAUNCH_PROMO_ACTIVE) {
                return {
                    ...plan,
                    description: "Get started with basic features, or sign up now to enjoy all PRO features for free during our launch!",
                    buttonText: "Sign Up (Pro Access Included!)",
                    buttonLink: "/register",
                };
            }
            return plan;
        });
    }

    return (
        <div className="min-vh-100 d-flex flex-column">
            {/* Efecto de Confeti */}
            {renderConfettiCanvas && windowSize.width > 0 && windowSize.height > 0 && (
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={recycleConfettiParticles} // Controlado por estado
                    numberOfPieces={recycleConfettiParticles ? 200 : 0} // Detiene la emisión de nuevas piezas cuando recycle es false
                    gravity={0.12} // Un poco más lento para un efecto de caída más visible
                    wind={0.01}    // Viento muy suave
                    style={{ zIndex: 1051, position: 'fixed', top: 0, left: 0 }}
                    onConfettiComplete={() => {
                        // Esta función se llama cuando recycle es false y todas las partículas han desaparecido.
                        // Es el momento seguro para dejar de renderizar el componente Confetti (el canvas).
                        setRenderConfettiCanvas(false);
                        console.log('Confetti animation complete, canvas removed.');
                    }}
                />
            )}

            <Header />
            <main className="flex-grow-1 py-5 bg-light">
                <Container className="py-5">
                    <Row className="text-center mb-4">
                        <Col>
                            <h1 className={`display-4 fw-bolder mb-3 ${IS_LAUNCH_PROMO_ACTIVE ? 'launch-title-animated' : ''}`}>{pageTitle}</h1>
                            <p className="lead text-muted fs-5 px-md-5">
                                {pageSubtitle}
                            </p>
                        </Col>
                    </Row>

                    {promoAlertMessage && (
                        <Row className="justify-content-center mb-4">
                            <Col md={10} lg={8}>
                                {promoAlertMessage}
                            </Col>
                        </Row>
                    )}

                    <Row className="justify-content-center g-4">
                        {effectivePricingPlans.map((plan) => (
                            <Col md={6} lg={4} key={plan.id} className="d-flex align-items-stretch">
                                <PricingCard
                                    planName={plan.planName}
                                    price={plan.price}
                                    originalPrice={plan.originalPrice}
                                    priceFrequency={plan.priceFrequency}
                                    description={plan.description}
                                    features={plan.features}
                                    buttonText={plan.buttonText}
                                    buttonVariant={plan.buttonVariant}
                                    buttonLink={plan.buttonLink}
                                    isPopular={plan.isPopular}
                                    promoNotice={plan.promoNotice}
                                    cardClassName="h-100"
                                />
                            </Col>
                        ))}
                    </Row>

                    {IS_LAUNCH_PROMO_ACTIVE && (
                        <Row className="text-center mt-5 pt-3">
                            <Col>
                                <p className="text-muted">
                                    After the promotional period (ending {LAUNCH_PROMO_END_DATE}), you can choose to subscribe to continue with Pro features,
                                    or your account will automatically transition to our standard Free plan.
                                </p>
                                <h5 className="fw-bold">
                                    No commitments. No credit card required during promo.
                                </h5>
                            </Col>
                        </Row>
                    )}
                </Container>
            </main>
            <Footer />
        </div>
    );
};

export default PricingPage;